import React from 'react';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';

import FormContainer from '../../../FormContainer';
import AddOrEditPageTabs from '../../../AddOrEditPageTabs';
import Table from '../../../../Table';
import AddBillForm from './AddBillForm';
import Documents from './ClaimDocuments';

import useClaimForm from '../../../../../forms/loan/claim';
import ClaimService from '../../../../../services/ClaimService';
import { useUiContext } from '../../../../../context/UiContext';
import useUserRole from '../../../../../hooks/useUserRole';

const tabs = [
  {
    label: 'Insurances',
  },
  {
    label: 'Claims',
    activeOn: 'Claim',
  },
];

export default function Claim({ loanId }) {
  const { showToast, showDialogModal } = useUiContext();

  const { resourceId } = useParams();
  const navigate = useNavigate();

  const isEdit = resourceId && resourceId !== 'new';

  const queryClient = useQueryClient();

  const { isLender } = useUserRole();

  const { data: claim, refetch } = useQuery({
    queryKey: ['claim', resourceId],
    queryFn: () => ClaimService.getClaim(resourceId),
    enabled: !!resourceId && resourceId !== 'new',
  });

  const { mutate: createClaim, isPending: isCreating } = useMutation({
    mutationFn: (formValues) =>
      ClaimService.createClaim({ formValues, loanId }),
    onSuccess: (clm) => {
      showToast({
        text: 'Claim created',
        type: 'success',
      });
      navigate(`/loans/${loanId}/claims/${clm.id}?tab=Claim`);
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { mutate: updateClaim, isPending: isUpdating } = useMutation({
    mutationFn: (formValues) =>
      ClaimService.updateClaim({ formValues, claimId: resourceId }),
    onSuccess: () => {
      showToast({
        text: 'Claim updated',
        type: 'success',
      });
      refetch();
      queryClient.invalidateQueries(['loanBillsForClaim', loanId]);
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const cols = [
    {
      title: 'Date',
      keys: ['created_at'],
      type: 'text',
      format: (value) => moment(value).format('M/D/YY'),
      link: (row) => `/loans/${row.loan_id}/bills/${row.id}?tab=Bill`,
    },
    {
      title: 'Amount',
      keys: ['_amount.total'],
      type: 'currency',
    },
    {
      title: 'Bill',
      keys: ['pixel_bill_number'],
      link: (row) => `/loans/${row.loan_id}/bills/${row.id}?tab=Bill`,
    },
    {
      title: 'Paid',
      keys: ['_paid.total'],
      type: 'currency',
    },
    {
      title: '',
      type: 'delete',
      onClick: async (row) => {
        showDialogModal({
          title: 'Confiramtion',
          message: 'Are you sure you want to remove this bill from this claim?',
          type: 'danger',
          onConfirm: () => {
            const billIds =
              claim.bill_id
                ?.filter((bill) => bill && bill.id !== row.id)
                .map((bill) => bill.id) || [];

            updateClaim({
              ...claim,
              bill_id: billIds,
            });
          },
          confirmButtonLabel: 'Yes, remove it',
          cancelButtonLabel: 'No, cancel',
        });
      },
    },
  ];

  const form = useClaimForm(
    isEdit ? updateClaim : createClaim,
    claim,
    loanId,
    isLender
  );

  return (
    <div>
      <div className="mb-4 flex justify-between md:items-center flex-col md:flex-row gap-2 h-[37px]">
        <AddOrEditPageTabs tabs={tabs} />
      </div>
      <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
        <div className="pt-[2.5px]">
          <FormContainer
            form={form}
            noBillComId
            groupSizeOffset={1}
            isSubmitting={isCreating || isUpdating}
          />
        </div>
        {isEdit && (
          <>
            <div className="mt-10 mb-10">
              <Table
                rows={claim?.bill_id || []}
                cols={cols}
                title="Claim Disbursements"
              />
            </div>
            <div className="mb-10">
              <AddBillForm
                claimId={resourceId}
                loanId={loanId}
                claim={claim}
                refetchClaim={refetch}
              />
            </div>
            <div className="mb-10">
              <Documents claim={claim} refetch={refetch} loanId={loanId} />
            </div>
            <Documents
              isPhoto
              claim={claim}
              refetch={refetch}
              loanId={loanId}
            />
          </>
        )}
      </div>
    </div>
  );
}
